import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicPetitionWizardContext } from '../../../hooks/useCaseRequestWizard';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import { isTodayOrFutureIsoDate } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { PublicPetitionWizardActions } from './PublicPetitionWizardActions';
import {
  publicPetitionWizardPath,
  PublicPetitionWizardShell,
  FieldError,
  inpClass } from './publicPetitionWizardShell';
import { usePublicPetitionLookups } from './usePublicPetitionLookups';
import {
  isHighUrgencyLevel,
  isPublicPetitionUrgencyJustificationRequired,
  minDeadlineDateForPublicPetition } from './publicPetitionHelpers';
import { validatePublicPetitionStep } from './publicPetitionStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;

export default function PublicPetitionStep3TimelinePage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = usePublicPetitionWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels } = usePublicPetitionLookups('step3');
  const minDeadlineDate = useMemo(() => minDeadlineDateForPublicPetition(), []);
  const urgencyJustificationRequired = isPublicPetitionUrgencyJustificationRequired(draft, urgencyLevels);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (!draft.urgencyLevelId || draft.urgencyLevelName || urgencyLevels.length === 0) return;
    const name = urgencyLevels.find((u) => u.id === draft.urgencyLevelId)?.name ?? '';
    if (name) setDraft((d) => ({ ...d, urgencyLevelName: name }));
  }, [draft.urgencyLevelId, draft.urgencyLevelName, urgencyLevels, setDraft]);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validatePublicPetitionStep(3, draft, { urgencyLevels });
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <PublicPetitionWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.urgencyLevel} <span className="req">*</span>
            </label>
            <WizardSelect
              id="pp-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              placeholder={f.selectUrgencyLevel}
              options={urgencyLevels.map((u) => ({ id: u.id, label: u.name }))}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d) => ({
                  ...d,
                  urgencyLevelId: id,
                  urgencyLevelName: name }));
                if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="pp-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d) => ({ ...d, deadline: next }));
                clearErr('deadline');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>
        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} {urgencyJustificationRequired ? <span className="req">*</span> : null}
            {!urgencyJustificationRequired ? (
              <span className="up-hint" style={{ fontWeight: 400, marginLeft: 6 }}>
                (Optional)
              </span>
            ) : null}
          </label>
          <textarea
            id="pp-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={3}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_TEXT) }));
            }}
            placeholder={
             'Reason the case needs urgent attention'
              
            }
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <PublicPetitionWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(publicPetitionWizardPath(2, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(publicPetitionWizardPath(4, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </PublicPetitionWizardShell>
  );
}
